import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_toast_inventory } from "../../../constants";
import { getDefaultAxiosConfig } from "../../../utilities/requests";

const empty = { available: false, snapshot: null, loading: false, error: null, reason: null };

// Probe only the selected location; never fan out across a customer's stores.
// Responses are scoped to the location and role, including during rapid switches.
export default function useToastInventory(storeId, permissionRole, active) {
  const internal = permissionRole === "rightwork_internal";
  const key = `${storeId}:${internal}`;
  const [state, setState] = useState(empty);
  const requestRef = useRef({ sequence: 0, cancel: null });

  const refresh = useCallback(async () => {
    requestRef.current.cancel?.();
    const sequence = ++requestRef.current.sequence;
    if (!internal || !storeId) {
      setState({ ...empty, key });
      return;
    }
    const source = axios.CancelToken.source();
    requestRef.current.cancel = source.cancel;
    setState(previous => ({ ...(previous.key === key ? previous : empty), key, loading: true, error: null }));
    try {
      const response = await axios.get(API_toast_inventory, {
        ...getDefaultAxiosConfig(),
        params: { store_id: storeId, availability_only: active ? "0" : "1" },
        cancelToken: source.token,
        timeout: 60000,
      });
      if (sequence !== requestRef.current.sequence) return;
      const payload = response.data?.redux?.payload;
      if (typeof payload?.available !== "boolean" ||
          (active && payload.available && !Array.isArray(payload.items))) {
        throw new Error("Invalid inventory response");
      }
      setState(previous => ({
        ...empty, key, available: payload.available, reason: payload.reason,
        snapshot: payload.available
          ? (active ? payload : previous.key === key ? previous.snapshot : null)
          : null,
      }));
    } catch (error) {
      if (axios.isCancel(error) || sequence !== requestRef.current.sequence) return;
      const denied = [401, 403].includes(error.response?.status);
      setState(previous => ({
        ...(denied || previous.key !== key ? empty : previous), key, loading: false,
        error: denied ? "Inventory is restricted to Rightwork employees at this location."
          : "Couldn’t refresh Toast inventory. Try again in a moment.",
      }));
    }
  }, [storeId, internal, key, active]);

  useEffect(() => {
    const request = requestRef.current;
    refresh();
    return () => {
      request.cancel?.();
      request.sequence += 1;
    };
  }, [refresh]);

  // Recover a failed background capability check without requiring a reload.
  useEffect(() => {
    if (!internal || active || state.key !== key || !state.error) return;
    const timer = setTimeout(refresh, 60000);
    return () => clearTimeout(timer);
  }, [internal, active, state.key, state.error, key, refresh]);

  return { ...(state.key === key && internal ? state : empty), refresh };
}
